import { useHistory } from 'react-router-dom';

import {
  AtlasGqlHorizonDamage,
  AtlasGqlHorizonDamageObservation,
  useGetHorizonDamagePictureQuery,
} from 'types/atlas-graphql';

import { Tooltip } from 'antd';
import { orderBy } from 'lodash';

import { Spinner } from 'components/Spinner';
import { ImageViewer } from 'components/ImageViewer';
import EmptyState from 'components/EmptyState';
import {
  StyledDiv,
  StyledCarouselWrapper,
  StyledCarousel,
  StyledQuestionCircleTwoTone,
  EmptyImage,
} from './HorizonDamageImage.style';
import COLORS from 'utils/color/definitions';

import { usePermissions } from 'utils/usePermissions';
import { WORK_ORDER_COLLABORATOR } from 'utils/access-control/roles';

import {
  getAttrsDistance,
  getPictureTargetDistance,
  getPrimaryObservationGroupForDamage,
} from 'horizon/components/Damages/utils';

type ImageProps = {
  damageId: string;
};

export const HorizonDamageImage: React.FunctionComponent<ImageProps> = ({ damageId }) => {
  const history = useHistory();
  const { HasRole } = usePermissions();
  const isWoCollaborator = HasRole({ role: WORK_ORDER_COLLABORATOR });

  // get picture data
  const { data, loading } = useGetHorizonDamagePictureQuery({
    variables: { input: { id: damageId } },
  });

  const damage = data?.getHorizonDamage && (data?.getHorizonDamage as AtlasGqlHorizonDamage);
  const primaryObservationGroup = damage && getPrimaryObservationGroupForDamage(damage);
  const observations = orderBy(
    primaryObservationGroup?.observations?.filter(
      (o): o is AtlasGqlHorizonDamageObservation => !!o
    ),
    [
      observation =>
        getAttrsDistance(observation?.attrs) ??
        getPictureTargetDistance(observation?.picture?.target),
      observation => observation?.attrs?.Severity ?? 0,
    ],
    ['asc', 'desc']
  );
  const primaryObservationGroupIndex =
    observations.findIndex(o => o.id === primaryObservationGroup?.sourceObservationId) ?? 0;

  const handleSelectObservation = (pictureId: string, observationId: string) => {
    if (pictureId) {
      history.push(
        `/pictures/${pictureId}${
          observationId ? `?observation=${observationId}&version=2` : '?version=2'
        }`,
        {
          useModal: true,
        }
      );
    }
  };

  return (
    <StyledDiv>
      {loading ? (
        <Spinner />
      ) : (
        observations && (
          <StyledCarouselWrapper data-testid="horizon-damage-carousel">
            <StyledCarousel initialSlide={primaryObservationGroupIndex}>
              {observations.map(observation => {
                const { picture } = observation;
                return (
                  <div key={observation.id}>
                    {picture ? (
                      <ImageViewer
                        id={picture.id}
                        image={picture.image}
                        target={picture.target}
                        homography={picture.homography}
                        isHorizonDamage={true}
                        defaultObservationId={observation.id}
                        selectObservation={() =>
                          handleSelectObservation(picture.id, observation.id)
                        }
                        observations={[{ ...observation, selected: true }]}
                      />
                    ) : (
                      <EmptyImage>
                        <EmptyState text="No Image Available" />
                      </EmptyImage>
                    )}
                  </div>
                );
              })}
            </StyledCarousel>
            {!isWoCollaborator && (damage?.observationGroups ?? []).length > 1 && (
              <Tooltip
                title={
                  <>
                    {observations.length === 1 ? 'This image is' : 'These images are'} from the
                    primary observation group. To see the full history of this damage, click on the
                    Damage ID.
                  </>
                }
              >
                <StyledQuestionCircleTwoTone twoToneColor={COLORS.TEAL} />
              </Tooltip>
            )}
          </StyledCarouselWrapper>
        )
      )}
    </StyledDiv>
  );
};
